import { ComponentDataSourceService } from '../../component-data-source.service';
import { DataSourceFunction } from '../../component-data-source.service';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { Observable, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { environment } from '../../../../environment/environment';
import { EventDTO } from '../../../../dto/event/event.dto';
import { Router } from '@angular/router';
import {
  fetchParticipationCanCancel$,
  fetchParticipationRegisterOpen$,
  PARTICIPATION_REGISTER_CLOSED_FALLBACK
} from './event-participation-register-open.utils';

const EVENT_GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Visibility data source for the event participate form.
 * Returns { visible: boolean } based on org setting "Tillad tilmelding af andre brugere til events" (events_allow_register_others).
 * Used with visibilityDataSource on a page or question to hide "add other users" when the org disables it.
 * Registration must also be open (GET participation/register-open), e.g. org deadline not passed.
 * Hidden when the user can self-cancel (can-cancel), so the cancel page (page6) is shown instead.
 */
export function event_allow_register_others(service: ComponentDataSourceService): DataSourceFunction {
  return {
    name: 'event_allow_register_others',
    description: 'Whether the current event\'s org allows registering other users (events_allow_register_others). For visibilityDataSource.',
    execute: (_params?: LoadDataParams): Observable<{ visible: boolean; message?: string }> => {
      const router = service.injector.get(Router);
      const urlWithoutQuery = (router.url || '').split('?')[0];
      const pathSegments = urlWithoutQuery.split('/');
      const eventGuid = pathSegments.find((segment) => EVENT_GUID_REGEX.test(segment));

      if (!eventGuid) {
        return of({ visible: true });
      }

      return fetchParticipationCanCancel$(service).pipe(
        switchMap((canCancel) => {
          if (canCancel) {
            return of({ visible: false });
          }
          return fetchParticipationRegisterOpen$(service, _params).pipe(
            switchMap(({ open, message }) => {
              if (!open) {
                return of({ visible: false, message: message || PARTICIPATION_REGISTER_CLOSED_FALLBACK });
              }
              return service.http.get<EventDTO>(`${environment.apiUrl}/events/${eventGuid}`).pipe(
                switchMap((eventData: EventDTO) => {
                  const eventAny = eventData as unknown as Record<string, unknown>;
                  const orgId = eventData.organizations_id ?? eventData.organization?.id ?? eventAny['organization_id'];
                  if (orgId == null) {
                    return of({ visible: true });
                  }
                  const obs = service.executeDataSource('org_settings', { organizationId: orgId });
                  if (!obs) {
                    return of({ visible: true });
                  }
                  return obs.pipe(
                    map((res: { data?: Record<string, unknown> }) => {
                      const value = res?.data?.['events_allow_register_others'];
                      const visible = value !== false && value !== 'false' && value !== 0;
                      return { visible };
                    })
                  );
                }),
                catchError((err) => {
                  console.warn('[event_allow_register_others] Error (event or org_settings):', err?.message || err, '→ visible: true (page will show)');
                  return of({ visible: true });
                })
              );
            }),
            catchError((err) => {
              console.warn('[event_allow_register_others] Error (register-open):', err?.message || err, '→ visible: true (page will show)');
              return of({ visible: true });
            })
          );
        })
      );
    }
  };
}
